<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import {
  createVocabularyBank,
  deleteVocabularyBank,
  importVocabularyBank,
  listVocabularyCatalog,
  listVocabularyBanks,
  updateVocabularyBank,
} from '@/services/backendService.js'

const tabs = [
  { id: 'known', label: 'Known Banks' },
  { id: 'focus', label: 'Focus Banks' },
]

const languageOptions = [
  { code: 'en', label: '🇬🇧 English' },
  { code: 'ja', label: '🇯🇵 Japanese' },
  { code: 'es', label: '🇪🇸 Spanish' },
  { code: 'ru', label: '🇷🇺 Russian' },
]

const languageLabelByCode = Object.fromEntries(
  languageOptions.map((language) => [language.code, language.label]),
)

const activeTab = ref('known')
const vocabularyBanks = ref([])
const catalogBanks = ref([])
const isLoading = ref(false)
const isCatalogLoading = ref(false)
const errorMessage = ref('')
const editingBankId = ref(null)
const savingBankId = ref(null)
const deletingBankId = ref(null)
const confirmingDeleteBankId = ref(null)
const deleteErrorBankId = ref(null)
const addMode = ref('manual')
const creatingBank = ref(false)
const createError = ref('')
const editError = ref('')
const deleteError = ref('')
const selectedCatalogBankId = ref('')
const createDraft = ref({
  title: '',
  language: 'en',
  level: '',
  topic: '',
  scope: 'known',
  wordsText: '',
})
const editDraft = ref({
  title: '',
  wordsText: '',
})

const knownBanks = computed(() =>
  vocabularyBanks.value.filter((bank) => ['known', 'kanji'].includes(bank.scope)),
)

const focusBanks = computed(() =>
  vocabularyBanks.value.filter((bank) => bank.scope === 'focus'),
)

const visibleBanks = computed(() =>
  activeTab.value === 'known' ? knownBanks.value : focusBanks.value,
)

const visibleCatalogBanks = computed(() =>
  catalogBanks.value.filter((bank) =>
    activeTab.value === 'known'
      ? ['known', 'kanji'].includes(bank.scope)
      : bank.scope === 'focus',
  ),
)

const importedCatalogBankIds = computed(
  () => new Set(vocabularyBanks.value.map((bank) => bank.id).filter(Boolean)),
)

const previewWords = (bank) => {
  if (!Array.isArray(bank.words)) {
    return ''
  }

  return bank.words.slice(0, 18).join(', ')
}

const formatBankLanguage = (language) => languageLabelByCode[language] ?? language

const isEditing = (bank) => editingBankId.value === bank.dbId

const parseWords = (wordsText) =>
  wordsText
    .split(/\r?\n|,/)
    .map((word) => word.trim())
    .filter(Boolean)

const resetCreateDraft = () => {
  createDraft.value = {
    title: '',
    language: 'en',
    level: '',
    topic: '',
    scope: activeTab.value,
    wordsText: '',
  }
}

const startEditing = (bank) => {
  editingBankId.value = bank.dbId
  confirmingDeleteBankId.value = null
  editError.value = ''
  deleteError.value = ''
  deleteErrorBankId.value = null
  editDraft.value = {
    title: bank.title,
    wordsText: Array.isArray(bank.words) ? bank.words.join('\n') : '',
  }
}

const cancelEditing = () => {
  editingBankId.value = null
  savingBankId.value = null
  editError.value = ''
  editDraft.value = {
    title: '',
    wordsText: '',
  }
}

const requestDeleteBankConfirmation = (bank) => {
  if (deletingBankId.value === bank.dbId) {
    return
  }

  deleteError.value = ''
  deleteErrorBankId.value = null
  confirmingDeleteBankId.value = bank.dbId
}

const cancelDeleteBankConfirmation = (bankId) => {
  if (confirmingDeleteBankId.value === bankId) {
    confirmingDeleteBankId.value = null
  }
}

const deleteBank = async (bank) => {
  try {
    deletingBankId.value = bank.dbId
    confirmingDeleteBankId.value = null
    deleteError.value = ''
    deleteErrorBankId.value = null
    await deleteVocabularyBank(bank.dbId)
    vocabularyBanks.value = vocabularyBanks.value.filter((item) => item.dbId !== bank.dbId)
    if (editingBankId.value === bank.dbId) {
      cancelEditing()
    }
  } catch (error) {
    console.error('Failed to delete vocabulary bank:', error)
    deleteError.value = 'Failed to delete vocabulary bank.'
    deleteErrorBankId.value = bank.dbId
  } finally {
    deletingBankId.value = null
  }
}

const saveBank = async (bank) => {
  const title = editDraft.value.title.trim()

  if (!title) {
    editError.value = 'Title is required.'
    return
  }

  try {
    savingBankId.value = bank.dbId
    editError.value = ''
    const updatedBank = await updateVocabularyBank(bank.dbId, {
      title,
      words: parseWords(editDraft.value.wordsText),
    })
    vocabularyBanks.value = vocabularyBanks.value.map((item) =>
      item.dbId === updatedBank.dbId ? updatedBank : item,
    )
    cancelEditing()
  } catch (error) {
    console.error('Failed to update vocabulary bank:', error)
    editError.value = 'Failed to save vocabulary bank.'
  } finally {
    savingBankId.value = null
  }
}

const createBank = async () => {
  const title = createDraft.value.title.trim()
  const language = createDraft.value.language.trim()
  const level = createDraft.value.level.trim()
  const topic = createDraft.value.topic.trim()
  const words = parseWords(createDraft.value.wordsText)

  if (!title || !language || !level || !topic) {
    createError.value = 'Title, language, level, and topic are required.'
    return
  }

  if (words.length === 0) {
    createError.value = 'Add at least one word.'
    return
  }

  try {
    creatingBank.value = true
    createError.value = ''
    const createdBank = await createVocabularyBank({
      title,
      language,
      level,
      topic,
      scope: createDraft.value.scope,
      words,
    })
    vocabularyBanks.value = [createdBank, ...vocabularyBanks.value]
    activeTab.value = createdBank.scope === 'focus' ? 'focus' : 'known'
    resetCreateDraft()
  } catch (error) {
    console.error('Failed to create vocabulary bank:', error)
    createError.value = 'Failed to create vocabulary bank.'
  } finally {
    creatingBank.value = false
  }
}

const importBank = async () => {
  if (!selectedCatalogBankId.value) {
    createError.value = 'Choose a catalog bank to import.'
    return
  }

  try {
    creatingBank.value = true
    createError.value = ''
    const createdBank = await importVocabularyBank(selectedCatalogBankId.value)
    vocabularyBanks.value = [createdBank, ...vocabularyBanks.value]
    activeTab.value = createdBank.scope === 'focus' ? 'focus' : 'known'
    selectedCatalogBankId.value = ''
  } catch (error) {
    console.error('Failed to import vocabulary bank:', error)
    createError.value = 'Failed to import vocabulary bank.'
  } finally {
    creatingBank.value = false
  }
}

watch(activeTab, (tab) => {
  if (['known', 'focus'].includes(createDraft.value.scope)) {
    createDraft.value.scope = tab
  }
})

onMounted(async () => {
  try {
    isLoading.value = true
    vocabularyBanks.value = await listVocabularyBanks()
  } catch (error) {
    console.error('Failed to load user vocabulary banks:', error)
    errorMessage.value = 'Failed to load your vocabulary banks.'
  } finally {
    isLoading.value = false
  }

  try {
    isCatalogLoading.value = true
    catalogBanks.value = await listVocabularyCatalog()
  } catch (error) {
    console.error('Failed to load vocabulary bank catalog:', error)
    createError.value = 'Failed to load vocabulary bank catalog.'
  } finally {
    isCatalogLoading.value = false
  }
})
</script>

<template>
  <section class="banks-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">Vocabulary</p>
        <h1>Your Banks</h1>
      </div>
      <p class="page-copy">
        Preview the banks currently attached to your account. Known banks hold vocabulary you
        already recognize; focus banks hold words you want to practice actively.
      </p>
    </header>

    <details class="add-bank-section">
      <summary class="add-bank-summary">
        <h2 id="add-bank-title">Add New Bank</h2>
        <span class="add-bank-indicator" aria-hidden="true">▾</span>
      </summary>

      <div class="add-bank-header">
        <div class="mode-switch" role="tablist" aria-label="Add bank mode">
          <button
            type="button"
            :class="['mode-button', { active: addMode === 'manual' }]"
            :aria-selected="addMode === 'manual'"
            @click="addMode = 'manual'"
          >
            From Scratch
          </button>
          <button
            type="button"
            :class="['mode-button', { active: addMode === 'catalog' }]"
            :aria-selected="addMode === 'catalog'"
            @click="addMode = 'catalog'"
          >
            From Catalog
          </button>
        </div>
      </div>

      <form v-if="addMode === 'manual'" class="add-bank-form" @submit.prevent="createBank">
        <div class="add-bank-fields">
          <label class="field-label">
            Title
            <input
              v-model="createDraft.title"
              class="title-input"
              type="text"
              maxlength="255"
              :disabled="creatingBank"
            />
          </label>
          <label class="field-label">
            Language
            <select v-model="createDraft.language" class="title-input" :disabled="creatingBank">
              <option
                v-for="language in languageOptions"
                :key="language.code"
                :value="language.code"
              >
                {{ language.label }}
              </option>
            </select>
          </label>
          <label class="field-label">
            Level
            <input
              v-model="createDraft.level"
              class="title-input"
              type="text"
              maxlength="32"
              :disabled="creatingBank"
            />
          </label>
          <label class="field-label">
            Topic
            <input
              v-model="createDraft.topic"
              class="title-input"
              type="text"
              maxlength="128"
              :disabled="creatingBank"
            />
          </label>
          <label class="field-label">
            Scope
            <select v-model="createDraft.scope" class="title-input" :disabled="creatingBank">
              <option value="known">Known</option>
              <option value="focus">Focus</option>
              <option value="kanji">Kanji</option>
            </select>
          </label>
        </div>

        <label class="field-label">
          Words
          <textarea
            v-model="createDraft.wordsText"
            class="words-input"
            rows="7"
            :disabled="creatingBank"
          />
        </label>

        <div class="edit-actions">
          <button class="action-button primary" type="submit" :disabled="creatingBank">
            {{ creatingBank ? 'Adding...' : 'Add Bank' }}
          </button>
        </div>
      </form>

      <form v-else class="add-bank-form" @submit.prevent="importBank">
        <label class="field-label">
          Catalog Bank
          <select
            v-model="selectedCatalogBankId"
            class="title-input"
            :disabled="creatingBank || isCatalogLoading"
          >
            <option value="">
              {{ isCatalogLoading ? 'Loading catalog...' : 'Choose a bank' }}
            </option>
            <option
              v-for="bank in visibleCatalogBanks"
              :key="bank.id"
              :value="bank.id"
              :disabled="importedCatalogBankIds.has(bank.id)"
            >
              {{ bank.title }} · {{ bank.wordCount }} words{{
                importedCatalogBankIds.has(bank.id) ? ' · added' : ''
              }}
            </option>
          </select>
        </label>

        <div class="edit-actions">
          <button
            class="action-button primary"
            type="submit"
            :disabled="creatingBank || isCatalogLoading"
          >
            {{ creatingBank ? 'Importing...' : 'Import Bank' }}
          </button>
        </div>
      </form>

      <p v-if="createError" class="edit-error">{{ createError }}</p>
    </details>

    <div class="tab-bar" role="tablist" aria-label="Vocabulary bank groups">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        :class="['tab-button', { active: activeTab === tab.id }]"
        :aria-selected="activeTab === tab.id"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <p v-if="isLoading" class="status-message">Loading vocabulary banks...</p>
    <p v-else-if="errorMessage" class="status-message error">{{ errorMessage }}</p>
    <p v-else-if="visibleBanks.length === 0" class="status-message">
      No vocabulary banks found in this tab.
    </p>

    <div v-else class="banks-grid">
      <article v-for="bank in visibleBanks" :key="bank.dbId" class="bank-card">
        <div class="bank-header">
          <div class="bank-actions">
            <span class="bank-scope">{{ bank.scope }}</span>
            <button
              v-if="!isEditing(bank) && confirmingDeleteBankId !== bank.dbId"
              class="action-button"
              type="button"
              :disabled="deletingBankId === bank.dbId"
              @click="startEditing(bank)"
            >
              Edit
            </button>
            <template v-if="!isEditing(bank) && confirmingDeleteBankId === bank.dbId">
              <button
                class="delete-bank-button danger"
                type="button"
                :disabled="deletingBankId === bank.dbId"
                @click="deleteBank(bank)"
              >
                {{ deletingBankId === bank.dbId ? 'Deleting...' : 'Confirm' }}
              </button>
              <button
                class="delete-bank-button cancel"
                type="button"
                :disabled="deletingBankId === bank.dbId"
                @click="cancelDeleteBankConfirmation(bank.dbId)"
              >
                Cancel
              </button>
            </template>
            <button
              v-else-if="!isEditing(bank)"
              class="delete-bank-button danger"
              type="button"
              :disabled="deletingBankId === bank.dbId"
              @click="requestDeleteBankConfirmation(bank)"
            >
              ❌
            </button>
          </div>
        </div>

        <div class="bank-title-block">
          <p class="bank-language">{{ formatBankLanguage(bank.language) }} · {{ bank.level }}</p>
          <h2 v-if="!isEditing(bank)">{{ bank.title }}</h2>
          <label v-else class="field-label">
            Title
            <input
              v-model="editDraft.title"
              class="title-input"
              type="text"
              maxlength="255"
              :disabled="savingBankId === bank.dbId"
            />
          </label>
        </div>
        <p v-if="deleteErrorBankId === bank.dbId" class="edit-error">{{ deleteError }}</p>

        <dl class="bank-meta">
          <div>
            <dt>Topic</dt>
            <dd>{{ bank.topic }}</dd>
          </div>
          <div>
            <dt>Words</dt>
            <dd>{{ bank.wordCount }}</dd>
          </div>
        </dl>

        <form v-if="isEditing(bank)" class="edit-panel" @submit.prevent="saveBank(bank)">
          <label class="field-label">
            Words
            <textarea
              v-model="editDraft.wordsText"
              class="words-input"
              rows="10"
              :disabled="savingBankId === bank.dbId"
            />
          </label>
          <p v-if="editError" class="edit-error">{{ editError }}</p>
          <div class="edit-actions">
            <button
              class="action-button primary"
              type="submit"
              :disabled="savingBankId === bank.dbId"
            >
              {{ savingBankId === bank.dbId ? 'Saving...' : 'Save' }}
            </button>
            <button
              class="action-button"
              type="button"
              :disabled="savingBankId === bank.dbId"
              @click="cancelEditing"
            >
              Cancel
            </button>
          </div>
        </form>

        <details v-else class="words-panel">
          <summary>Preview words</summary>
          <div class="words-panel-content">
            <p class="word-preview">
              {{ previewWords(bank) || 'This bank does not contain previewable words yet.' }}
            </p>
            <div class="word-list">
              <span
                v-for="(word, wordIndex) in bank.words"
                :key="`${bank.dbId}-${wordIndex}`"
                class="word-chip"
              >
                {{ word }}
              </span>
            </div>
          </div>
        </details>
      </article>
    </div>
  </section>
</template>

<style scoped>
.banks-page {
  padding: 2rem 1.5rem 3rem;
}

.page-header {
  display: grid;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.eyebrow {
  margin: 0 0 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.75rem;
  color: #7acb8a;
}

h1 {
  margin: 0;
  font-size: clamp(2rem, 4vw, 3rem);
  color: #f6f7f2;
}

.page-copy {
  margin: 0;
  max-width: 60ch;
  color: #c3c8be;
  line-height: 1.5;
}

.tab-bar {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.tab-button {
  border: 1px solid #36503d;
  background: #16201a;
  color: #dce7dc;
  border-radius: 999px;
  padding: 0.7rem 1.1rem;
  cursor: pointer;
  font-weight: 600;
}

.tab-button.active {
  background: #4caf50;
  border-color: #4caf50;
  color: #08120a;
}

.add-bank-section {
  border-top: 1px solid rgba(122, 203, 138, 0.18);
  border-bottom: 1px solid rgba(122, 203, 138, 0.18);
  margin-bottom: 1.5rem;
}

.add-bank-section[open] {
  padding-bottom: 1rem;
}

.add-bank-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  cursor: pointer;
  padding: 1rem 0;
  color: #f6f7f2;
}

.add-bank-summary::marker {
  color: #9fe2ab;
}

.add-bank-summary h2 {
  margin-left: 0.5rem;
  font-size: 1.15rem;
}

.add-bank-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid rgba(174, 224, 182, 0.28);
  border-radius: 999px;
  color: #9fe2ab;
  transition: transform 0.18s ease;
}

.add-bank-section[open] .add-bank-indicator {
  transform: rotate(180deg);
}

.add-bank-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.mode-switch {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.mode-button {
  border: 1px solid rgba(174, 224, 182, 0.28);
  background: rgba(255, 255, 255, 0.06);
  color: #dce7dc;
  border-radius: 999px;
  padding: 0.55rem 0.85rem;
  cursor: pointer;
  font-weight: 700;
}

.mode-button.active {
  background: rgba(122, 203, 138, 0.2);
  border-color: #9fe2ab;
  color: #f6f7f2;
}

.add-bank-form {
  display: grid;
  gap: 0.9rem;
}

.add-bank-fields {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;
}

.status-message {
  color: #c3c8be;
}

.status-message.error {
  color: #ff9e9e;
}

.banks-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.bank-card {
  background: linear-gradient(180deg, rgba(39, 55, 45, 0.96), rgba(19, 27, 22, 0.98));
  border: 1px solid rgba(122, 203, 138, 0.18);
  border-radius: 22px;
  padding: 1.2rem;
  color: #eef3eb;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22);
}

.bank-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;
}

.bank-title-block {
  flex: 1;
  min-width: 0;
  margin-bottom: 1rem;
}

.bank-language {
  margin: 0 0 0.35rem;
  color: #9bcfa2;
  font-size: 0.9rem;
}

h2 {
  margin: 0;
  font-size: 1.1rem;
}

.bank-actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
}

.bank-scope {
  align-self: start;
  margin-right: auto;
  background: rgba(122, 203, 138, 0.12);
  color: #9fe2ab;
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  font-size: 0.85rem;
  text-transform: capitalize;
}

.action-button {
  border: 1px solid rgba(174, 224, 182, 0.38);
  background: rgba(255, 255, 255, 0.08);
  color: #eef3eb;
  border-radius: 8px;
  min-height: 2.2rem;
  padding: 0.45rem 0.8rem;
  cursor: pointer;
  font-weight: 700;
  white-space: nowrap;
}

.action-button:hover:not(:disabled) {
  border-color: #9fe2ab;
  background: rgba(122, 203, 138, 0.16);
}

.action-button:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.action-button.primary {
  background: #4caf50;
  border-color: #4caf50;
  color: #08120a;
}

.delete-bank-button {
  flex-shrink: 0;
  border: 1px solid rgba(76, 175, 80, 0.34);
  background: rgba(76, 175, 80, 0.1);
  color: #b5e5bf;
  border-radius: 8px;
  min-height: 2.2rem;
  padding: 0.45rem 0.8rem;
  cursor: pointer;
  font-weight: 700;
  white-space: nowrap;
}

.delete-bank-button:hover:not(:disabled) {
  border-color: #4caf50;
  background: rgba(76, 175, 80, 0.18);
}

.delete-bank-button:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.delete-bank-button.cancel {
  border-color: rgba(111, 120, 110, 0.3);
  background: rgba(111, 120, 110, 0.1);
  color: #d2d9d2;
}

.delete-bank-button.danger {
  border-color: rgba(248, 113, 113, 0.38);
  background: rgba(248, 113, 113, 0.12);
  color: #fecaca;
}

.delete-bank-button.danger:hover:not(:disabled) {
  border-color: #f87171;
  background: rgba(248, 113, 113, 0.2);
}

.bank-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 0 0 1rem;
}

.bank-meta dt {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #8fa195;
  margin-bottom: 0.25rem;
}

.bank-meta dd {
  margin: 0;
  font-weight: 600;
}

.edit-panel {
  display: grid;
  gap: 0.9rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 0.9rem;
}

.field-label {
  display: grid;
  gap: 0.4rem;
  color: #aee0b6;
  font-size: 0.85rem;
  font-weight: 700;
}

.title-input,
.words-input {
  width: 100%;
  border: 1px solid rgba(174, 224, 182, 0.28);
  background: rgba(7, 14, 10, 0.62);
  color: #f6f7f2;
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
  font: inherit;
}

select.title-input {
  min-height: 2.7rem;
}

.title-input:focus,
.words-input:focus {
  border-color: #9fe2ab;
  outline: none;
}

.words-input {
  min-height: 12rem;
  line-height: 1.45;
  resize: vertical;
}

.edit-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.edit-error {
  margin: 0;
  color: #ff9e9e;
  font-weight: 600;
}

.words-panel {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 0.9rem;
}

.words-panel summary {
  cursor: pointer;
  font-weight: 600;
  color: #aee0b6;
}

.words-panel-content {
  max-height: 18rem;
  overflow-y: auto;
  padding-right: 0.25rem;
  scrollbar-gutter: stable;
}

.word-preview {
  color: #d2dbcf;
  line-height: 1.5;
}

.word-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.85rem;
}

.word-chip {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  font-size: 0.88rem;
}

@media (max-width: 700px) {
  .banks-page {
    padding: 1.2rem 1rem 2rem;
  }

  .bank-meta {
    grid-template-columns: 1fr;
  }

  .bank-actions {
    flex-wrap: wrap;
  }

  .add-bank-header {
    align-items: stretch;
    flex-direction: column;
  }

  .add-bank-fields {
    grid-template-columns: 1fr;
  }
}
</style>
